import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import type { GlobalSearchResult } from '../services/types';
import { formatDate, formatMoney, subStatusLabel } from '../utils/format';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const wrapRef = useRef<HTMLDivElement>(null);

  const runSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    const res = await api.globalSearch(q.trim());
    if (res.data) setResults(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }
    timer.current = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(timer.current);
  }, [query, runSearch]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const hasResults =
    results &&
    (results.users.length > 0 ||
      results.payments.length > 0 ||
      results.vpn_keys.length > 0 ||
      results.invites.length > 0 ||
      results.promos.length > 0);

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
      <input
        className="input"
        style={{ width: '100%' }}
        type="search"
        placeholder="Глобальный поиск: ID, username, payment, VPN, promo, invite…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && query.trim().length >= 2 && (
        <div className="search-dropdown">
          {loading && <div className="search-item">Поиск…</div>}
          {!loading && !hasResults && (
            <div className="search-item" style={{ color: 'var(--text-muted)' }}>
              Ничего не найдено
            </div>
          )}
          {!loading && results && results.users.length > 0 && (
            <>
              <div className="search-group-title">Пользователи</div>
              {results.users.map((u) => (
                <Link
                  key={u.user_id}
                  className="search-item"
                  to={`/users/${u.user_id}`}
                  onClick={() => setOpen(false)}
                >
                  #{u.user_id}{' '}
                  {u.username ? `@${u.username}` : u.first_name ?? '—'} ·{' '}
                  {subStatusLabel(u.subscription_status)}
                </Link>
              ))}
            </>
          )}
          {!loading && results && results.payments.length > 0 && (
            <>
              <div className="search-group-title">Платежи</div>
              {results.payments.map((p) => (
                <Link
                  key={p.id}
                  className="search-item"
                  to={`/users/${p.user_id}`}
                  onClick={() => setOpen(false)}
                >
                  {p.id} · user {p.user_id} · {formatMoney(p.amount)} · {p.status}
                </Link>
              ))}
            </>
          )}
          {!loading && results && results.vpn_keys.length > 0 && (
            <>
              <div className="search-group-title">VPN ключи</div>
              {results.vpn_keys.map((k) => (
                <Link
                  key={k.id}
                  className="search-item"
                  to={`/users/${k.user_id}`}
                  onClick={() => setOpen(false)}
                >
                  {k.server_country.toUpperCase()} · client {k.client_id} · user {k.user_id}
                </Link>
              ))}
            </>
          )}
          {!loading && results && results.invites.length > 0 && (
            <>
              <div className="search-group-title">Инвайты</div>
              {results.invites.map((i) => (
                <Link
                  key={i.code}
                  className="search-item"
                  to={`/users/${i.owner_id}`}
                  onClick={() => setOpen(false)}
                >
                  {i.code} · owner {i.owner_id} · {i.status}
                </Link>
              ))}
            </>
          )}
          {!loading && results && results.promos.length > 0 && (
            <>
              <div className="search-group-title">Промокоды</div>
              {results.promos.map((p) => (
                <Link
                  key={`${p.code}-${p.user_id}`}
                  className="search-item"
                  to={`/users/${p.user_id}`}
                  onClick={() => setOpen(false)}
                >
                  {p.code} · user {p.user_id} · {formatDate(p.used_at)}
                </Link>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
